import * as React from 'react';
import IBike from './IBike';
import './bike.css';
import { Link } from 'react-router-dom';
import { useTranslation, getI18n } from 'react-i18next';

/**
 * BikeDiv component.
 * 
 * This is a functional component that renders a single bike's details. 
 * It uses the `useTranslation` hook from `react-i18next` to provide internationalization.
 * 
 * Props:
 * - Bike: An object representing a bike and its details.
 * 
 * State:
 * - shortNotes: A shortened version of the bike's notes, if the notes are longer than 50 characters.
 * 
 * Functions:
 * - shortNotes: If the bike's notes are longer than 50 characters, it shortens them and adds an ellipsis at the end.
 * 
 * The `useTranslation` hook returns a `t` function that you can use to get the translated text.
 * The keys passed to the `t` function ('bikeDiv.price' and 'bikeDiv.viewMore') 
 * correspond to keys in the translation files.
 * 
 * @returns The BikeDiv component.
 */
export default function BikeDiv(Bike: IBike) {
    const { t } = useTranslation();
    const currentLanguage = getI18n().language;
    const [imageError, setImageError] = React.useState(false);
    var thumbnailUrl = 'https://bikes.blob.core.windows.net/bikeimages/' + Bike.id + '_thumb.jpg';
    var fullUrl = 'https://bikes.blob.core.windows.net/bikeimages/' + Bike.id + '.jpg';
    var notes = (currentLanguage === 'en' ? Bike.notesEn : Bike.notes) || "";
    var shortNotes = notes;
    if (notes.length > 50) {
        shortNotes = notes.substring(0, 50);
        shortNotes = shortNotes + "...";
    }

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (!imageError) {
            setImageError(true);
            e.currentTarget.src = fullUrl;
        }
    };

    return (
        <div className="BikeDiv" >
            <img src={thumbnailUrl} onError={handleImageError} width="640" height="480" style={{ width: '100%', height: 'auto' }} alt={`${Bike.brand || 'Polkupyörä'} ${Bike.model || ''}`.trim()} loading="lazy" />
            <h2>{Bike.year} {Bike.brand} {Bike.model} {Bike.tireSize}″</h2>
            <h3>{t('bikeDiv.price')} {Bike.price}€</h3>
            <h3>{shortNotes}</h3>
            <Link to={'/bike/' + Bike.id} style={{ textDecoration: 'none' }}>
                {t('bikeDiv.viewMore')}
            </Link>
        </div>
    )
}
